import { BomGeneratorAbstract } from '../../bom_generators/BomGeneratorAbstract'
import { Item, MountingCalcResult, MountingComponentType } from '../../types'
import { addItems, createMountingItem } from '../../utils'
import {
  getFoundationPointCount,
  lookupAllQuantities,
  TABLE_1INP_SINGLE,
  TABLE_1INP_SINGLE_HIGH_WIND,
  TABLE_1INP_TWIN,
  TABLE_1INP_TWIN_HIGH_WIND,
  TABLE_2INP_HIGH_WIND,
  TABLE_2INP_STANDARD,
} from './SolarPortBomLookups'
import { autoFetchDlubalUKDataIfNeeded } from '../ukDlubal'
import {
  BomLookupTable,
  COMPONENT_CODES_1INP,
  COMPONENT_CODES_2INP,
  DEFAULT_END_CLAMP_DEPTH,
  FOUNDATION_ITEMS,
  PANEL_HEIGHT_THRESHOLD_MM,
  PANEL_LIMITS,
  SolarPortComponentKey,
  SolarPortConfig,
  SolarPortFoundationType,
  SolarPortPostType,
  SolarPortRelativeTilt,
} from './SolarPortTypes'

export class SolarPortBomGenerator extends BomGeneratorAbstract {
  private addWarning(message: string): void {
    const system = window.editor.selectedSystem
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message,
      key: 'SOLARPORTGROUNDMOUNT_PLUGIN_WARNING',
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  }

  async generateBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    await autoFetchDlubalUKDataIfNeeded()

    const config = (this.input.options.portraitConfig as SolarPortConfig) ?? '1inP'
    const postType = (this.input.options.postType as SolarPortPostType) ?? 'singlePost'
    const relativeTilt = (this.input.options.relativeTilt as SolarPortRelativeTilt) ?? 30
    const foundation = this.input.options.foundation as SolarPortFoundationType | undefined
    const windZone = (this.input.options.windZone as string) ?? 'high'
    const isHighWind = windZone === 'high'

    const panelCount = this.countActivePanels()
    if (panelCount === 0) return { ...result, items: [] }

    const activeRowCount = this.block.rows.filter((row) => row.some((panel) => panel.isActive)).length

    if (config === '2inP') {
      if (activeRowCount !== 2) {
        this.addWarning(window.translate('Panel layout must have 2 rows for 2 in Portrait'))
        return { ...result, items: [] }
      }
      const rowPanelCounts = this.block.rows
        .map((row) => row.filter((panel) => panel.isActive).length)
        .filter((c) => c > 0)
      if (rowPanelCounts[0] !== rowPanelCounts[1]) {
        this.addWarning(
          window.translate('Panel layout must have an equal number of panels in both rows for 2 in Portrait')
        )
        return { ...result, items: [] }
      }
    }

    if (config === '1inP' && activeRowCount !== 1) {
      this.addWarning(window.translate('Panel layout must have 1 row for 1 in Portrait'))
      return { ...result, items: [] }
    }

    const limits = PANEL_LIMITS[config]
    if (panelCount < limits.min || panelCount > limits.max) {
      const message =
        config === '1inP'
          ? 'Minimum of 3 and maximum of 30 panels with selected mounting system'
          : 'Minimum of 8 and maximum of 60 panels with selected mounting system'
      this.addWarning(window.translate(message))
      return { ...result, items: [] }
    }

    const table = this.selectTable(config, postType, isHighWind)
    const panelHeightMm = this.block.panel.height
    const panelDepthBucket = this.getPanelDepthBucket()

    const items: Item[] = []
    const quantities = lookupAllQuantities(table, panelCount)

    for (const [key, qty] of Object.entries(quantities)) {
      const code = this.resolveComponentCode(
        key as SolarPortComponentKey,
        config,
        relativeTilt,
        panelHeightMm,
        panelDepthBucket
      )
      items.push(...this.createItems(code, qty))
    }

    if (foundation) {
      const foundationPoints = getFoundationPointCount(table, panelCount)
      const foundationItemDefs = FOUNDATION_ITEMS[foundation]
      if (foundationItemDefs && foundationPoints > 0) {
        for (const def of foundationItemDefs) {
          items.push(...this.createItems(def.code, def.quantity * foundationPoints))
        }
      }
    }

    return addItems(result, items)
  }

  private countActivePanels(): number {
    let count = 0
    for (const row of this.block.rows) {
      for (const panel of row) {
        if (panel.isActive) count++
      }
    }
    return count
  }

  private selectTable(config: SolarPortConfig, postType: SolarPortPostType, isHighWind: boolean): BomLookupTable {
    if (config === '2inP') {
      return isHighWind ? TABLE_2INP_HIGH_WIND : TABLE_2INP_STANDARD
    }
    if (postType === 'twinPost') {
      return isHighWind ? TABLE_1INP_TWIN_HIGH_WIND : TABLE_1INP_TWIN
    }
    return isHighWind ? TABLE_1INP_SINGLE_HIGH_WIND : TABLE_1INP_SINGLE
  }

  private getPanelDepthBucket(): number {
    const thickness = this.block.panel.thickness ?? DEFAULT_END_CLAMP_DEPTH
    return thickness > 30 ? 35 : 30
  }

  private resolveComponentCode(
    key: SolarPortComponentKey,
    config: SolarPortConfig,
    relativeTilt: SolarPortRelativeTilt,
    panelHeightMm: number,
    panelDepthBucket: number
  ): string {
    const codes = config === '1inP' ? COMPONENT_CODES_1INP : COMPONENT_CODES_2INP

    switch (key) {
      case 'rafter':
        if (config === '2inP') {
          const rafterCodes = COMPONENT_CODES_2INP.rafter
          return panelHeightMm > PANEL_HEIGHT_THRESHOLD_MM ? rafterCodes.tall : rafterCodes.standard
        }
        return COMPONENT_CODES_1INP.rafter

      case 'rearUpright':
        return codes.rearUpright[relativeTilt] ?? codes.rearUpright[30]

      case 'endClampCap':
        return codes.endClampCap[panelDepthBucket] ?? codes.endClampCap[30]

      case 'endClampHex':
        return codes.endClampHex[panelDepthBucket] ?? codes.endClampHex[30]

      case 'frontUpright':
        return codes.frontUpright as string

      default:
        return codes[key] as string
    }
  }

  private createItems(code: string, quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: code,
      components: [{ name: code, type: MountingComponentType.OTHER, left: 0, top: 0 }],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }
}
